import React, { useState, useRef } from 'react';
import APIService from '../services/api';

const BASE = 'https://besoins-tat-production.up.railway.app';

// Petit helper icone, aligne sur le reste de l'app (Bootstrap Icons)
const BI = ({ icon, className = '' }) => <i className={`bi bi-${icon} ${className}`} />;

export default function ImportCandidats({ showNotification }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);   // { total, nouveaux, maj, apercu: [...] }
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);         // { crees, majs, total }
  const inputRef = useRef(null);

  const authHeaders = { Authorization: `Bearer ${APIService.getToken()}` };

  const reset = () => { setFile(null); setPreview(null); setDone(null); if (inputRef.current) inputRef.current.value = ''; };

  const handleFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.csv')) {
      showNotification?.('Merci de déposer un fichier CSV', 'error');
      return;
    }
    setFile(f);
    setPreview(null);
    setDone(null);
    runPreview(f);
  };

  const runPreview = async (f) => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await fetch(`${BASE}/candidats/import/preview`, { method: 'POST', headers: authHeaders, body: fd });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Analyse échouée');
      setPreview(json.data);
    } catch (e) {
      showNotification?.(`Erreur d'analyse : ${e.message}`, 'error');
      reset();
    } finally {
      setLoading(false);
    }
  };

  const runCommit = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${BASE}/candidats/import/commit`, { method: 'POST', headers: authHeaders, body: fd });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Import échoué');
      setDone(json.data);
      showNotification?.(json.message || 'Import terminé', 'success');
    } catch (e) {
      showNotification?.(`Erreur d'import : ${e.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center">
          <BI icon="cloud-upload-fill" className="me-2 text-indigo-600" /> Importer des candidats
        </h1>
        <p className="text-gray-500 text-sm mt-1">Export CSV depuis BoondManager · format détecté automatiquement</p>
      </div>

      {/* Zone de depot */}
      {!done && (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
          onClick={() => inputRef.current?.click()}
          className="bg-white border-2 border-dashed border-gray-200 hover:border-indigo-300 rounded-xl p-8 text-center cursor-pointer transition"
        >
          <input ref={inputRef} type="file" accept=".csv" className="hidden"
                 onChange={(e) => handleFile(e.target.files?.[0])} />
          <BI icon="file-earmark-arrow-up" className="text-4xl text-gray-300" />
          <p className="mt-3 text-gray-700 font-medium">{file ? file.name : 'Glisse ton fichier CSV ici, ou clique pour parcourir'}</p>
          {file && <p className="text-xs text-gray-400 mt-1">{(file.size / 1024).toFixed(1)} Ko</p>}
        </div>
      )}

      {loading && (
        <div className="mt-6 text-center text-gray-500 text-sm">
          <BI icon="arrow-repeat" className="animate-spin me-2" /> Traitement en cours…
        </div>
      )}

      {/* Apercu avant validation */}
      {preview && !done && !loading && (
        <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex gap-3 mb-5">
            <div className="flex-1 bg-gray-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-semibold text-gray-800">{preview.total}</div>
              <div className="text-xs text-gray-500">détectés</div>
            </div>
            <div className="flex-1 bg-green-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-semibold text-green-700">{preview.nouveaux}</div>
              <div className="text-xs text-green-600">nouveaux</div>
            </div>
            <div className="flex-1 bg-blue-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-semibold text-blue-700">{preview.maj}</div>
              <div className="text-xs text-blue-600">mises à jour</div>
            </div>
          </div>

          <p className="text-sm font-medium text-gray-700 mb-2">Aperçu</p>
          <div className="border border-gray-100 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="text-left font-medium px-3 py-2">Candidat</th>
                  <th className="text-left font-medium px-3 py-2">Titre</th>
                  <th className="text-left font-medium px-3 py-2">Ville</th>
                  <th className="text-right font-medium px-3 py-2">État</th>
                </tr>
              </thead>
              <tbody>
                {preview.apercu.map((c, i) => (
                  <tr key={i} className="border-t border-gray-100">
                    <td className="px-3 py-2 text-gray-800">{[c.prenom, c.nom].filter(Boolean).join(' ')}</td>
                    <td className="px-3 py-2 text-gray-500 truncate">{c.titre || '—'}</td>
                    <td className="px-3 py-2 text-gray-500">{c.ville || '—'}</td>
                    <td className="px-3 py-2 text-right">
                      {c.etat === 'maj'
                        ? <span className="text-xs text-blue-700 bg-blue-50 px-2 py-0.5 rounded">màj</span>
                        : <span className="text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded">nouveau</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {preview.total > preview.apercu.length && (
            <p className="text-xs text-gray-400 mt-2">… et {preview.total - preview.apercu.length} autre(s)</p>
          )}
          <p className="text-xs text-gray-400 mt-3">
            <BI icon="info-circle" className="me-1" />
            Les candidats déjà importés (même référence Boond) seront mis à jour, pas dupliqués.
          </p>

          <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-gray-100">
            <button onClick={reset} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition">Annuler</button>
            <button onClick={runCommit} disabled={loading}
                    className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium transition flex items-center">
              <BI icon="download" className="me-2" /> Importer {preview.total} candidat{preview.total > 1 ? 's' : ''}
            </button>
          </div>
        </div>
      )}

      {/* Resultat */}
      {done && (
        <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
          <BI icon="check-circle-fill" className="text-4xl text-green-500" />
          <p className="mt-3 text-lg font-medium text-gray-800">Import terminé</p>
          <p className="text-gray-500 text-sm mt-1">{done.crees} créé(s) · {done.majs} mis à jour</p>
          <button onClick={reset} className="mt-5 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition">
            Importer un autre fichier
          </button>
        </div>
      )}
    </div>
  );
}
